/* global console */
import React from "react";
import type { LabelProps } from "@fluentui/react-label";
import type { DropdownProps } from "@fluentui/react-combobox";
import type { SelectProps } from "@fluentui/react-select";
import type { SpinButtonProps } from "@fluentui/react-spinbutton";
import { tokens } from "@fluentui/react-theme";
import { useId } from "@fluentui/react-utilities";
import { makeStyles } from "@griffel/react";
import { useTranslation } from "react-i18next";
import { SelectValueOption } from "../domain/SelectValueOption";
import { FilterOptions } from "../domain/FilterOptions";
import { FilterQueryParameters } from "../domain/FilterQueryParameters";
import { Room } from "../domain/Room";
import { getInitialFilterOptions } from "../../service/ActionHandler";
import SelectValueFilter from "./SelectValueFilter";
import SpinButtonFilter from "./SpinButtonFilter";
import SelectMultiFilter from "./SelectMultiFilter";

const updateFilter = (
  currentOptions: SelectValueOption[],
  newOptions: SelectValueOption[],
  newValue: string,
  setOptions: (options: SelectValueOption[]) => void,
  setValue: (value: string) => void
) => {
  if (newOptions.length > 0 || currentOptions.length > 0) {
    setOptions(newOptions);
    setValue(newValue);
  }
};

const useStyles = makeStyles({
  filterGroupStyle: {
    display: "grid",
    gridTemplateColumns: "auto auto",
    rowGap: tokens.spacingVerticalS,
    columnGap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    marginLeft: tokens.spacingHorizontalXL,
    marginRight: tokens.spacingHorizontalXXXL,
  },
  filterLabelStyle: {
    alignSelf: "center",
  },
  filterControlStyle: {
    // nothing extra
  },
  multiFilterStyle: {
    marginLeft: tokens.spacingHorizontalXL,
    marginRight: tokens.spacingHorizontalXXXL,
    marginTop: tokens.spacingVerticalM,
  },
  multiFilterControlStyle: {
    marginTop: tokens.spacingVerticalS,
    textAlign: "center",
  },
});

const selectProps: SelectProps = {
  size: "medium",
};
const labelProps: LabelProps = {
  size: "small",
};
const spinButtonProps: SpinButtonProps = {
  size: "medium",
};
const selectMultiProps: DropdownProps = {
  size: "small",
  appearance: "underline",
};

interface RoomFilterProps {
  buildingId: string;
  setBuildingId: (buildingId: string) => void;

  floorId: string;
  setFloorId: (floorId: string) => void;

  arrangeTypeId: string;
  setArrangeTypeId: (arrangeTypeId: string) => void;

  capacity: number | null;
  setCapacity: (capacity: number | null) => void;

  selectedAttributes: string[];
  setSelectedAttributes: (attributes: string[]) => void;

  loading: boolean;
  setLoading: (loading: boolean) => void;

  setRooms: (rooms: Room[]) => void;

  initialized: boolean;
  setInitialized: (initialized: boolean) => void;
}

let initStarted = false;
let initialFilterOptions = new FilterOptions();

const RoomFilters = (props: RoomFilterProps): React.JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();

  const selectCountryId = useId();
  const [countries, setCountries] = React.useState([] as SelectValueOption[]);
  const [countryId, setCountryId] = React.useState("");

  const selectStateId = useId();
  const [states, setStates] = React.useState([] as SelectValueOption[]);
  const [stateId, setStateId] = React.useState("");

  const selectCityId = useId();
  const [cities, setCities] = React.useState([] as SelectValueOption[]);
  const [cityId, setCityId] = React.useState("");

  const selectSiteId = useId();
  const [sites, setSites] = React.useState([] as SelectValueOption[]);
  const [siteId, setSiteId] = React.useState("");

  const selectBuildingId = useId();
  const [buildings, setBuildings] = React.useState([] as SelectValueOption[]);

  const selectFloorId = useId();
  const [floors, setFloors] = React.useState([] as SelectValueOption[]);

  const selectArrangementId = useId();
  const [arrangeTypes, setArrangeTypes] = React.useState([] as SelectValueOption[]);

  const capacityFieldId = useId();

  const selectAttributesId = useId();
  const [availableAttributes, setAvailableAttributes] = React.useState([] as SelectValueOption[]);

  const buildCountryQuery = (value: string) => {
    return new FilterQueryParameters({
      levels: initialFilterOptions.getFilterSubLevels("country"),
      ctry_id: value,
    });
  };
  const updateCountryFilter = (data: FilterOptions) => {
    setCountryId(data.ctry_id);
    updateFilter(states, data.states, data.state_id, setStates, setStateId);
    updateFilter(cities, data.cities, data.city_id, setCities, setCityId);
    updateFilter(sites, data.sites, data.site_id, setSites, setSiteId);
    updateFilter(buildings, data.buildings, data.bl_id, setBuildings, props.setBuildingId);
    updateFilter(floors, data.floors, data.fl_id, setFloors, props.setFloorId);
  };

  const buildStateQuery = (value: string) => {
    return new FilterQueryParameters({
      levels: initialFilterOptions.getFilterSubLevels("state"),
      state_id: value,
    });
  };
  const updateStateFilter = (data: FilterOptions) => {
    setStateId(data.state_id);
    updateFilter(cities, data.cities, data.city_id, setCities, setCityId);
    updateFilter(sites, data.sites, data.site_id, setSites, setSiteId);
    updateFilter(buildings, data.buildings, data.bl_id, setBuildings, props.setBuildingId);
    updateFilter(floors, data.floors, data.fl_id, setFloors, props.setFloorId);
  };

  const buildCityQuery = (value: string) => {
    return new FilterQueryParameters({
      levels: initialFilterOptions.getFilterSubLevels("city"),
      state_id: stateId,
      city_id: value,
    });
  };
  const updateCityFilter = (data: FilterOptions) => {
    setCityId(data.city_id);
    updateFilter(sites, data.sites, data.site_id, setSites, setSiteId);
    updateFilter(buildings, data.buildings, data.bl_id, setBuildings, props.setBuildingId);
    updateFilter(floors, data.floors, data.fl_id, setFloors, props.setFloorId);
  };

  const buildSiteQuery = (value: string) => {
    return new FilterQueryParameters({
      levels: initialFilterOptions.getFilterSubLevels("site"),
      site_id: value,
    });
  };
  const updateSiteFilter = (data: FilterOptions) => {
    setSiteId(data.site_id);
    updateFilter(buildings, data.buildings, data.bl_id, setBuildings, props.setBuildingId);
    updateFilter(floors, data.floors, data.fl_id, setFloors, props.setFloorId);
  };

  const buildBuildingQuery = (value: string) => {
    return new FilterQueryParameters({
      levels: initialFilterOptions.getFilterSubLevels("building"),
      bl_id: value,
    });
  };
  const updateBuildingFilter = (data: FilterOptions) => {
    props.setBuildingId(data.bl_id);
    updateFilter(floors, data.floors, data.fl_id, setFloors, props.setFloorId);
  };

  const buildFloorQuery = (value: string) => {
    return new FilterQueryParameters({
      levels: initialFilterOptions.getFilterSubLevels("floor"),
      bl_id: props.buildingId,
      fl_id: value,
    });
  };
  const updateFloorFilter = (data: FilterOptions) => {
    props.setFloorId(data.fl_id);
  };

  const buildArrangementQuery = (value: string) => {
    return new FilterQueryParameters({
      levels: initialFilterOptions.getFilterSubLevels("arrangement"),
      rm_arrange_type_id: value,
    });
  };
  const updateArrangementFilter = (data: FilterOptions) => {
    props.setArrangeTypeId(data.rm_arrange_type_id);
  };

  // quick test to ensure it's only called once
  if (!initStarted) {
    getInitialFilterOptions()
      .then((data: FilterOptions) => {
        initialFilterOptions = data;
        updateFilter(countries, data.countries, data.ctry_id, setCountries, setCountryId);
        updateFilter(states, data.states, data.state_id, setStates, setStateId);
        updateFilter(cities, data.cities, data.city_id, setCities, setCityId);
        updateFilter(sites, data.sites, data.site_id, setSites, setSiteId);
        updateFilter(buildings, data.buildings, data.bl_id, setBuildings, props.setBuildingId);
        updateFilter(floors, data.floors, data.fl_id, setFloors, props.setFloorId);
        updateFilter(arrangeTypes, data.arrangements, data.rm_arrange_type_id, setArrangeTypes, props.setArrangeTypeId);
        setAvailableAttributes(data.attributes);
        props.setInitialized(true);
      })
      .catch((reason: any) => {
        console.log("Backend unreachable. Room Filters could not be initialized.");
        console.log(reason);
        // TODO show 'offline' warning somewhere, depending on reason?
        // refresh of room list remains blocked while initialized = false
      })
      .finally(() => {
        props.setLoading(false);
      });
    initStarted = true;
  }

  if (!props.initialized) {
    return <></>;
  }

  return (
    <>
      <div className={styles.filterGroupStyle}>
        <SelectValueFilter
          controlId={selectCountryId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("country")}
          selectOptions={countries}
          selectProps={selectProps}
          selectedValue={countryId}
          disabled={props.loading}
          setDisabled={props.setLoading}
          buildQueryParameters={buildCountryQuery}
          updateFilters={updateCountryFilter}
        />
        <SelectValueFilter
          controlId={selectStateId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("state")}
          selectOptions={states}
          selectProps={selectProps}
          selectedValue={stateId}
          disabled={props.loading}
          setDisabled={props.setLoading}
          buildQueryParameters={buildStateQuery}
          updateFilters={updateStateFilter}
        />
        <SelectValueFilter
          controlId={selectCityId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("city")}
          selectOptions={cities}
          selectProps={selectProps}
          selectedValue={cityId}
          disabled={props.loading}
          setDisabled={props.setLoading}
          buildQueryParameters={buildCityQuery}
          updateFilters={updateCityFilter}
        />
        <SelectValueFilter
          controlId={selectSiteId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("site")}
          selectOptions={sites}
          selectProps={selectProps}
          selectedValue={siteId}
          disabled={props.loading}
          setDisabled={props.setLoading}
          buildQueryParameters={buildSiteQuery}
          updateFilters={updateSiteFilter}
        />
        <SelectValueFilter
          controlId={selectBuildingId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("building")}
          selectOptions={buildings}
          selectProps={selectProps}
          selectedValue={props.buildingId}
          disabled={props.loading}
          setDisabled={props.setLoading}
          buildQueryParameters={buildBuildingQuery}
          updateFilters={updateBuildingFilter}
        />
        <SelectValueFilter
          controlId={selectFloorId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("floor")}
          selectOptions={floors}
          selectProps={selectProps}
          selectedValue={props.floorId}
          disabled={props.loading}
          setDisabled={props.setLoading}
          buildQueryParameters={buildFloorQuery}
          updateFilters={updateFloorFilter}
        />
        <SelectValueFilter
          controlId={selectArrangementId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("arrangement")}
          selectOptions={arrangeTypes}
          selectProps={selectProps}
          selectedValue={props.arrangeTypeId}
          disabled={props.loading}
          setDisabled={props.setLoading}
          buildQueryParameters={buildArrangementQuery}
          updateFilters={updateArrangementFilter}
        />
        <SpinButtonFilter
          controlId={capacityFieldId}
          controlStyle={styles.filterControlStyle}
          labelProps={labelProps}
          labelStyle={styles.filterLabelStyle}
          labelText={t("capacity")}
          spinButtonProps={spinButtonProps}
          value={props.capacity}
          setValue={props.setCapacity}
          disabled={props.loading}
        />
      </div>
      <SelectMultiFilter
        filterStyle={styles.multiFilterStyle}
        controlId={selectAttributesId}
        controlStyle={styles.multiFilterControlStyle}
        labelProps={labelProps}
        labelStyle={styles.filterLabelStyle}
        labelText={t("attributes")}
        placeholderText={t("select_attributes")}
        selectOptions={availableAttributes}
        selectProps={selectMultiProps}
        selectedOptions={props.selectedAttributes}
        setSelectedOptions={props.setSelectedAttributes}
        disabled={props.loading}
      />
    </>
  );
};

export default RoomFilters;
